import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useAsync } from "react-use";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useSubscribe } from "replicache-react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import Compressor from "@uppy/compressor";
import { DashboardModal, useUppy } from "@uppy/react";
import ImageEditor from "@uppy/image-editor";
import { OrgChartSideDataForSidePanel } from "state-atoms";
import { OrgChartElement, NAME_ID, PHOTO_ID, ROLE_ID } from "shared/datamodel/schemas/org-chart";
import { debounce } from "frontend/utils/debounce";
import { getReadSignedUrl, getWriteSignedUrl } from "frontend/api";
import { buildUploadUrl } from "frontend/utils/file-utils";
import { useBoardValue } from "frontend/state/board-state";
import { Floater } from "frontend/ui-components/floaters/floater";
import { PortalID } from "frontend/modal/portal-types";
import style from "./orgchart-side-panel.module.css";
import useOutsideRef from "frontend/utils/click-outside-handler";
import { TrashIcon } from "frontend/icons/trash-icon";
import { ClosedEyeIcon } from "frontend/icons/closed-eye-icon";
import { OpenedEyeIcon } from "frontend/icons/opened-eye-icon";
import { produceWithPatches } from "immer";
import { customAlphabet } from "nanoid";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { keys } from "rambda";
import type { EventFor } from "frontend/utils/fn-utils";
import { getPathPrefix } from "../../utils/getPathPrefix";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { Box } from "frontend/ui-components/design-system/box/box";

const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const nanoid = customAlphabet(alphabet, 10);

/**
 * Close icon (x) for the side panel
 */
const CloseIcon = ({ onClick }: { onClick: () => void }) => (
  <svg
    data-testid="orgchart-sidepanel-close-icon"
    className={style.closeIcon}
    onClick={onClick}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M1 1 L11 11 M11 1 L1 11" stroke="currentColor" strokeWidth="1" strokeLinecap="square" />
  </svg>
);

/**
 * Context menu icon
 */
const ContextMenuIcon = () => (
  <svg width="16" height="100%" viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="1.6" cy="1.6" r="1.6" fill="currentColor" />
    <circle cx="8" cy="1.6" r="1.6" fill="currentColor" />
    <circle cx="14.4" cy="1.6" r="1.6" fill="currentColor" />
  </svg>
);

function Dropdown({
  relativeTo,
  onDismiss,
  children,
}: {
  relativeTo: React.RefObject<HTMLElement> | HTMLElement;
  onDismiss: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<any>();
  useOutsideRef(ref, onDismiss);

  const portal = document.getElementById(PortalID.Floaters);
  const element = (
    <Floater ref={ref} relativeTo={relativeTo} side={"bottom"} boundary="none" className={style.dropdown} margin={0}>
      {children}
    </Floater>
  );
  return portal ? ReactDOM.createPortal(element, portal) : element;
}

// I might not need the expires field. once the browser has loaded the image once, it caches it.
// just asking for the same url (even after expiry) should bring up the cached image.
// and if it doesn't (browser cache was cleared) I'll get an error and I'll just ask for a new url.
// this cache can be used for all images in the canvas, not just orgchart.
const cache: any = {};
async function getReadUrl(syncService: SyncService<RW> | null, shortUrl?: string | null) {
  if (!shortUrl) return null;

  if (shortUrl.startsWith(getPathPrefix("/images/orgchart/"))) return shortUrl;
  if (cache[shortUrl] && cache[shortUrl].expires > Date.now() + 1000 * 60) {
    return cache[shortUrl].url;
  }

  let url;
  if (shortUrl.includes("/")) {
    url = await getReadSignedUrl(shortUrl);
  } else {
    url = await syncService
      ?.getReplicache()
      ?.query(async (tx) => tx.get("cMetadata-File-" + shortUrl))
      .then((value: any) => getReadSignedUrl(value.url));
  }
  cache[shortUrl] = url;
  return url.url;
}

function OrgChartSidePanelContent({
  syncService,
  patchCanvasElement,
  onClose,
  onUpgradeRequired,
  nodeData,
}: {
  syncService: SyncService<RW> | null;
  patchCanvasElement: (args: { id: string; patch: any; inversePatch?: any; details?: any }) => void;
  onClose: () => void;
  onUpgradeRequired?: () => void;
  nodeData: OrgChartSideDataForSidePanel;
}) {
  const [{ board, user }] = useBoardValue();

  const { rootid, selectedNode, editable } = nodeData;
  const isCimReorderEnabled = useIsCIMReorderEnabled();

  const orgchartRoot = useSubscribe(
    syncService?.getReplicache(),
    async (tx) => tx.get("cElement-" + rootid),
    "not-loaded",
    [rootid]
  );

  const trackEvent = (evt: string, details = "") =>
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.ORG_CHART,
      evt,
      details,
      board?.documentId,
      board?.accountId,
      user?.id
    );

  // Create a getter function that will be used by uppy (uppy constructor is run once, and
  // captures reference to this function, so it will always get the latest values of node and orgchartRoot)
  const getCurrentInfo = useRef<any>(null);
  getCurrentInfo.current = () => ({ selectedNode, rootid, orgchartRoot, board, trackEvent });

  const uppy = useUppy(() =>
    new Uppy({
      debug: false,
      restrictions: {
        maxFileSize: 15 * 1024 * 1024, //limit upload size to 15MB
        maxNumberOfFiles: 1,
        minNumberOfFiles: 0,
        allowedFileTypes: ["image/*"],
      },
      meta: {
        url: "",
        rootId: "",
        nodeId: "",
      },
    })
      .use(AwsS3, {
        companionUrl: `${process.env.HOST}/companion`,
        metaFields: ["name", "type", "url", "rootId", "nodeId"],
        getUploadParameters(file) {
          const url = (file.meta as any).url;
          return getWriteSignedUrl(url, file.type!);
        },
      })
      .use(Compressor)
      .use(ImageEditor, {
        id: "ImageEditor",
        actions: {
          revert: true,
          rotate: true,
          granularRotate: true,
          flip: true,
          zoomIn: true,
          zoomOut: true,
          cropSquare: false,
          cropWidescreen: false,
          cropWidescreenVertical: false,
        },
        cropperOptions: {
          croppedCanvasOptions: {},
          viewMode: 1,
          aspectRatio: 1,
          center: false,
          guides: false,
          responsive: true,
        },
      })
      .on("file-added", (file) => {
        const { selectedNode, rootid, board } = getCurrentInfo.current();
        if (!board) {
          console.warn("I don't have board data");
          return;
        }
        file.meta = {
          name: file.name,
          type: file.type,
          url: buildUploadUrl(board.accountId, board.documentId, file.name),
          rootId: rootid,
          nodeId: selectedNode,
        };
      })
      .on("complete", (result) => {
        result.successful.forEach((file) => {
          const { url, rootId, nodeId } = file.meta as any;
          const { orgchartRoot, trackEvent } = getCurrentInfo.current();
          trackEvent("orgchart_picture_upload_done");
          const [_, patch, inversePatch] = produceWithPatches(orgchartRoot as OrgChartElement, (draft) => {
            draft.data[nodeId][PHOTO_ID] = url;
          });
          patchCanvasElement({ id: rootId, patch, inversePatch });
          uppy.removeFile(file.id);
        });
      })
  );

  if (orgchartRoot == "not-loaded") {
    return null;
  }
  if (
    !orgchartRoot ||
    !selectedNode ||
    orgchartRoot.hidden ||
    !orgchartRoot.layout?.[selectedNode] ||
    !orgchartRoot.data?.[selectedNode]
  ) {
    onClose();
    return null;
  }

  if (isCimReorderEnabled) {
    return (
      <Box className={style.cimContainer}>
        <CloseIcon onClick={onClose} />
        <EditingArea
          syncService={syncService}
          key={selectedNode}
          patchCanvasElement={patchCanvasElement}
          elementId={rootid}
          element={orgchartRoot}
          selectedNode={selectedNode}
          uppy={uppy}
          onUpgradeRequired={onUpgradeRequired}
          trackEvent={trackEvent}
          editable={editable}
        />
      </Box>
    );
  }

  const marginTop = document.getElementById("app-header-container")?.clientHeight || 100;
  return (
    <div
      className={style.container}
      style={{
        marginTop,
        height: `calc(100% - ${marginTop}px)`,
      }}
    >
      <CloseIcon onClick={onClose} />
      <EditingArea
        syncService={syncService}
        key={selectedNode}
        patchCanvasElement={patchCanvasElement}
        elementId={rootid}
        element={orgchartRoot}
        selectedNode={selectedNode}
        uppy={uppy}
        onUpgradeRequired={onUpgradeRequired}
        trackEvent={trackEvent}
        editable={editable}
      />
    </div>
  );
}

function EditingArea({
  syncService,
  patchCanvasElement,
  elementId,
  element,
  selectedNode,
  uppy,
  onUpgradeRequired,
  trackEvent,
  editable,
}: {
  syncService: SyncService<RW> | null;
  patchCanvasElement: (args: { id: string; patch: any; inversePatch?: any; details?: any }) => void;
  elementId: string;
  element: OrgChartElement;
  selectedNode: string;
  uppy: Uppy;
  onUpgradeRequired?: () => void;
  trackEvent: (evt: string, details?: string) => void;
  editable: boolean;
}) {
  const [dropdownAnchor, setDropdownAnchor] = useState<any>(null);
  const changeset = useRef<any>({ fieldNames: null, valueChanges: null });
  const portal = document.getElementById(PortalID.Tooltips);

  useEffect(() => trackEvent("orgchart_editor_opened"), []);

  const readUrl = useAsync(
    () => getReadUrl(syncService, element.data[selectedNode][PHOTO_ID]),
    [element.data[selectedNode][PHOTO_ID]]
  );
  const [showImageUpload, setShowImageUpload] = useState(false);
  const nodeData = element.data[selectedNode];

  function onAddField() {
    if (onUpgradeRequired) {
      onUpgradeRequired();
      return;
    }
    trackEvent("orgchart_add_new_field_clicked", keys(element.extraFields).length.toString());
    const id = nanoid();
    const [_, patch, inversePatch] = produceWithPatches(element, (draft) => {
      draft.extraFields[id] = { id, type: "string", name: "", hidden: false };
      draft.orderOfFields.push(id);
    });
    patchCanvasElement({ id: elementId, patch, inversePatch });
  }

  function onDeleteField(ev: EventFor<"input", "onClick">) {
    const fieldId = ev.currentTarget.dataset.id;
    if (!fieldId) {
      console.warn("you forgot to attach id to button");
      return;
    }
    trackEvent("orgchart_field_delete", keys(element.extraFields).length.toString());
    const [_, patch, inversePatch] = produceWithPatches(element, (draft) => {
      delete draft.extraFields[fieldId];
      draft.orderOfFields.splice(draft.orderOfFields.indexOf(fieldId), 1);
      // can also delete the data from the nodes, but I don't think it's necessary
    });
    patchCanvasElement({ id: elementId, patch, inversePatch });
  }

  // TODO: curry all productWithPatches functions
  function onHideShowField(ev: EventFor<"input", "onClick">) {
    const fieldId = ev.currentTarget.dataset.id;
    if (!fieldId) {
      console.warn("you forgot to attach id to button");
      return;
    }
    const [_, patch, inversePatch] = produceWithPatches(element, (draft) => {
      draft.extraFields[fieldId].hidden = !draft.extraFields[fieldId].hidden;
    });
    patchCanvasElement({ id: elementId, patch, inversePatch });
    trackEvent(element.extraFields[fieldId].hidden ? "orgchart_field_display_added" : "orgchart_field_display_removed");
  }

  function onDragEnd(result: DropResult) {
    if (result.destination) {
      const [_, patch, inversePatch] = produceWithPatches(element, (draft) => {
        const src = result.source.index;
        const dest = result.destination!.index;
        [draft.orderOfFields[dest], draft.orderOfFields[src]] = [draft.orderOfFields[src], draft.orderOfFields[dest]];
      });
      patchCanvasElement({ id: elementId, patch, inversePatch });
      trackEvent("orgchart_field_dragged");
    }
  }

  function sendChanges() {
    const { fieldNames, valueChanges } = changeset.current;
    const [_, patch, inversePatch] = produceWithPatches(element, (draft) => {
      if (fieldNames) {
        for (const fieldId in fieldNames) {
          if (fieldId in draft.extraFields) {
            draft.extraFields[fieldId].name = fieldNames[fieldId];
          }
        }
        trackEvent("orgchart_field_name_changed");
      }
      if (valueChanges) {
        for (const fieldId in valueChanges) {
          draft.data[selectedNode][fieldId] = valueChanges[fieldId];
        }
        trackEvent("orgchart_field_text_changed");
      }
    });
    patchCanvasElement({ id: elementId, patch, inversePatch });
    changeset.current = { fieldNames: null, valueChanges: null };
  }

  const sendChangesDebounced = debounce(sendChanges);

  const onFieldTitleChange = (ev: EventFor<"input", "onChange">) => {
    const fieldId = ev.currentTarget.name;
    const value = ev.currentTarget.value;
    changeset.current.fieldNames ??= {};
    changeset.current.fieldNames[fieldId] = value;
    sendChangesDebounced();
  };

  const onFieldValueChange = (ev: EventFor<"input", "onChange">) => {
    const fieldId = ev.currentTarget.name;
    const value = ev.currentTarget.value;
    changeset.current.valueChanges ??= {};
    changeset.current.valueChanges[fieldId] = value;
    sendChangesDebounced();
  };

  const onKeyUp = (ev: EventFor<"input", "onKeyUp">) => {
    if (ev.key == "Enter" && !ev.shiftKey && !ev.ctrlKey && !ev.altKey && !ev.metaKey) {
      const fieldId = ev.currentTarget.name;
      const value = ev.currentTarget.value;
      changeset.current.valueChanges ??= {};
      changeset.current.valueChanges[fieldId] = value;
      sendChanges();
      ev.currentTarget.blur();
    }
  };

  function renderHeader() {
    return (
      <>
        <button
          className={style.photo}
          onClick={() => {
            trackEvent("orgchart_picture_edit");
            setShowImageUpload(true);
          }}
          disabled={!editable}
        >
          {nodeData[PHOTO_ID] && readUrl.loading == false && readUrl.value ? (
            <div className={style.pictureContainer} data-editable={editable}>
              <img src={readUrl.value} width="100%" height="100%" alt="user photo" />
              <div className={style.hoverOverlay}>
                <svg xmlns="http://www.w3.org/2000/svg" width="33px" height="37px" fill="white" viewBox="0 0 33 37">
                  <circle cx="15.7" cy="10.3" r="9.5" />
                  <path d="M32 37a16 16 0 1 0-32 0h32Z" />
                </svg>
                Change pic
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "2px", alignItems: "center" }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="33px" height="37px" fill="#DADCE0" viewBox="0 0 33 37">
                <circle cx="15.7" cy="10.3" r="9.5" />
                <path d="M32 37a16 16 0 1 0-32 0h32Z" />
              </svg>
              {editable && <span style={{ fontFamily: "Poppins", fontSize: "10px", lineHeight: 1.4 }}>Upload pic</span>}
            </div>
          )}
        </button>

        <input
          key={NAME_ID}
          className={style.name}
          name={NAME_ID}
          defaultValue={nodeData[NAME_ID] ?? ""}
          onChange={onFieldValueChange}
          onKeyUp={onKeyUp}
          placeholder="Employee Name"
          disabled={!editable}
        />
        <input
          key={ROLE_ID}
          className={style.role}
          name={ROLE_ID}
          defaultValue={nodeData[ROLE_ID] ?? ""}
          onChange={onFieldValueChange}
          onKeyUp={onKeyUp}
          placeholder="Role"
          disabled={!editable}
        />
      </>
    );
  }

  const uploadModal = (
    <DashboardModal
      uppy={uppy}
      animateOpenClose={true}
      closeModalOnClickOutside={true}
      closeAfterFinish={true}
      autoOpenFileEditor={true}
      showRemoveButtonAfterComplete={false}
      open={showImageUpload}
      onRequestClose={() => setShowImageUpload(false)}
      plugins={["ImageEditor"]}
    />
  );

  return (
    <div className={style.editingArea}>
      <div className={style.nodeHeader}>{renderHeader()}</div>
      {/* user defined fields */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{ overflowY: "auto", paddingBottom: "25px" }}>
          <Droppable droppableId="droppable" direction="vertical">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {element.orderOfFields.map((id: string, index: number) => {
                  if (!element.extraFields[id]) return null;
                  const name = element.extraFields[id].name;
                  return (
                    <Draggable key={id} draggableId={id} index={index} isDragDisabled={!editable}>
                      {(provided) => (
                        <div
                          className={style.field}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style}
                        >
                          {editable && (
                            <div className={style.handle} {...provided.dragHandleProps}>
                              &#x22EE;&#x22EE;
                            </div>
                          )}
                          <input
                            autoFocus={true}
                            className={style.title}
                            name={id}
                            defaultValue={name ?? ""}
                            placeholder={`Field ${index + 1}`}
                            onChange={onFieldTitleChange}
                            onKeyUp={onKeyUp}
                            disabled={!editable}
                          />
                          <div className={style.inputContainer}>
                            <input
                              name={id}
                              style={{ width: "100%" }}
                              defaultValue={nodeData[id] ?? ""}
                              placeholder="Add value.."
                              onChange={onFieldValueChange}
                              onKeyUp={onKeyUp}
                              disabled={!editable}
                            />
                            {editable && (
                              <div
                                className={style.btn}
                                data-active={dropdownAnchor?.id == id}
                                role="button"
                                onClick={(e) => {
                                  const target = e.currentTarget; // must save this, since events are GCed by browser
                                  setDropdownAnchor((prev: any) =>
                                    prev?.element == target ? null : { element: target, id: id }
                                  );
                                }}
                              >
                                <ContextMenuIcon />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {dropdownAnchor != null && (
            <Dropdown relativeTo={dropdownAnchor.element} onDismiss={() => setDropdownAnchor(null)}>
              <label>
                <input type="button" data-id={dropdownAnchor.id} onClick={onHideShowField} />
                {element.extraFields[dropdownAnchor.id].hidden ? (
                  <>
                    <ClosedEyeIcon size={20} />
                    <span>Show</span>
                  </>
                ) : (
                  <>
                    <OpenedEyeIcon size={20} />
                    <span>Hide</span>
                  </>
                )}
              </label>

              <label>
                <input
                  type="button"
                  data-id={dropdownAnchor.id}
                  onClick={(ev) => {
                    onDeleteField(ev);
                    setDropdownAnchor(null); // the element is deleted, so kill the dropdown
                  }}
                />
                <TrashIcon size={20} />
                <span>Delete</span>
              </label>
            </Dropdown>
          )}
          {editable && (
            <button className={style.addFieldButton} onClick={onAddField}>
              + Add new field
            </button>
          )}
        </div>
      </DragDropContext>
      {portal ? ReactDOM.createPortal(uploadModal, portal) : uploadModal}
    </div>
  );
}

export default React.memo(OrgChartSidePanelContent);
